import * as _ from 'lodash-es';

import { GO } from './common';
import { EntityGroup, FittingEntity } from './entity';
import {
  FIELD_A0,
  FIELD_DF,
  FIELD_NM,
  FIELD_ST,
  FIELD_ID,
  FIELD_SN,
  FIELD_Z0,
  FIELD_MP,
  FIELD_L0,
  FIELD_W1,
  FIELD_H1,
  FIELD_D1,
  FIELD_S0,
  FIELD_HAS_EC,
  FIELD_HAS_INSULATION,
  FIELD_USE_DIAGONAL,
  FIELD_SIDE_TAKE_OFF,
  FIELD_ADD_STO,
  FIELD_PARENT_ID,
  LENGTH_STR_FIELD,
  LENGTH_FIELD,
  VERTICAL_LENGTH_FIELD,
} from './fields';
import {
  CATALOGUE_CATEGORY as CAT,
  DUCTSHAPES as SP,
  LENGTH_TYPE,
  SPECIAL_JOB_ITEM,
  SYMBOL_REVISION as REV,
} from './options';

export const DEFAULT_ALL_SHORT = 'all';
export const DEFAULT_EMPTIY_OPT = 'empty';

// for pipe fitting such as elbow-pipe
// @2025/08/19
export const ANGLE_VALUES = ['90', '60', '45', '30'];

export const coarseUnitSuffix = [VERTICAL_LENGTH_FIELD, FIELD_L0];
export const readonlyFields = [LENGTH_STR_FIELD];
/**
 * FIXME: hard-coded round shape ducts used in new duct dialog
 * @date 2024/03/20
 */
export const roundDucts = ['ROUND_DUCT_UP', 'ROUND_DUCT_DOWN'];

export const EQUIPMENT_FIELDS_IN_ORDER = [
  FIELD_NM,
  FIELD_ST,
  FIELD_ID,
  FIELD_SN,
  FIELD_Z0,
  FIELD_MP,
];

/** Option type for DimensionSelect - supports {label, value} objects */
type SelectOption = { label: string; value: string };

/** Options can be string[] or {label, value}[] for backward compatibility */
export type SelectOptions = string[] | SelectOption[];

/** Normalize options to always be {label, value}[] format */
export const normalizeOptions = (options?: SelectOptions): SelectOption[] => {
  if (!options) return [];
  if (options.length === 0) return [];
  // Check if first item is a string to determine format
  if (typeof options[0] === 'string') {
    return (options as string[]).map((opt) => ({
      label: opt || 'EMPTY', // ! add empty option @2026/06/08
      value: opt,
    }));
  }
  return options as SelectOption[];
};

/** only use valid options - @2026/06/11 */
export const purgeEmptyOption = (opts: string[]) => {
  return opts.filter((opt) => !!opt);
};

/**
 * == symbol invisible fields ==
 * @2026/05/06
 */
const APPENDIX_FIELDS = [
  FIELD_HAS_EC,
  FIELD_USE_DIAGONAL,
  FIELD_SIDE_TAKE_OFF,
  FIELD_ADD_STO,
  FIELD_PARENT_ID,
  FIELD_HAS_INSULATION,
  //  TODO add more...
];

/**
 * fields no need to `SHOW` at symbol properties panel & `review panel`
 */
export const OMIT_FROM_SHOW = [LENGTH_FIELD, ...APPENDIX_FIELDS];
/**
 * fields no need to `EDIT`(display as input)
 * @2025/04/22
 */
export const OMIT_FROM_EDIT = [LENGTH_STR_FIELD, ...OMIT_FROM_SHOW];

/**
 * fields excluded to calculate completion percentage value
 */
const OMMIT_FROM_COMPLETION = [
  FIELD_SN, // this is a field of `EQUIPMENT` symbol, ignored to calculate completion
  FIELD_Z0, // @2025/04/03
  ...APPENDIX_FIELDS,
];

export const OMIT_FROM_ROUND = [
  'height',
  'height_1',
  'height_2',
  'height_3',
  'width',
  'width_1',
  'width_2',
  'width_3',
  `length_1`,
];

export const OMIT_FROM_SQUARE = [
  'angle', // FIXME: add `angle` for `ELBOW_PIPING` symbol @2024/07/02
  'diameter',
  'diameter_1',
  `diameter_2`,
  `diameter_3`,
  `length_1`,
  // ! do not make sense ... @2025/02/27
  // `vertical_length`, // for `ENDPOINT_TYPE_A` @2025/01/14
];

/**
 * fields doesnt needed in `ROUNDRECT` shape
 * @2025/09/08
 */
export const OMIT_FROM_ROUNDRECT = [
  'angle',
  'diameter',
  'height',
  'width',
  `diameter_2`,
  `diameter_3`,
  'height_2',
  'height_3',
  'width_2',
  'width_3',
  `length_1`,
];

/**
 * ! Omited dimensions from `round-rect` shape of _REDUCER
 * @2025/07/16
 */
const OMIT_FROM_RDRC = ['height_2', 'width_2', 'diameter_2', 'length_1'];

/**
 * check shape options in side panel
 * @2025/07/03
 *
 * add FLEX shape
 * @2026/03/24
 *
 * use a full shape list
 * @2026/06/09
 */
export const findShapeOptionsBy = (entityType: string) => {
  return [SP.EMPTY, SP.FLEX, SP.ROUND, SP.SQUARE, SP.RNDREC];
};

export const hasRoundRect = (entityType: string) => {
  const isReducer = entityType.includes('_REDUCER');
  return isReducer;
};

/**
 * Infer the `shape` implied by focusing an empty diameter/width/height
 * dimension field, so the correct dimension fields (see
 * `getReducedFieldsByShape`) show up before the user picks a shape
 * explicitly.
 *
 * ECCENTRIC_REDUCER only supports RECT | ROUNDRECT — it has no ROUND shape —
 * so a diameter focus infers ROUNDRECT there instead of the usual ROUND.
 * @2026/09/22
 */
export const inferShapeFromFocusedField = (
  field: string,
  entityType?: string,
) => {
  const isDiameterField = field.includes('diameter');
  if (!isDiameterField) return SP.SQUARE;

  const isEccentricReducer = entityType === SPECIAL_JOB_ITEM.ECR;
  return isEccentricReducer ? SP.RNDREC : SP.ROUND;
};

/**
 * Check if the duct is round, then display width/height input only in new duct dialog
 *
 * @date 2024/03/21
 * @param itemType
 * @returns
 */
export const checkDuctRoundOnly = (itemType: string) => {
  return roundDucts.includes(itemType);
};

/**
 * Exclude the unrelevant fields in calculation of completion
 *
 * @param fields dimension fields to calculate completion
 *
 * @returns necessary fields to calculate
 */
export const filterCompletionFields = (fields: string[]) => {
  return _.difference(fields, OMMIT_FROM_COMPLETION);
};

/**
 * Back compatible shape checking method for `round|ROUND`
 * @param shape
 * @returns
 */
export const isRoundShape = (shape: string | undefined) => {
  return (
    shape == SP.ROUND || shape?.toUpperCase() == SP.ROUND || shape == 'Round'
  );
};

/**
 * Back compatible shape checking method for `rect|RECT|rectangular`
 * @param shape
 * @returns
 */
export const isRectShape = (shape: string | undefined) => {
  return (
    shape == SP.SQUARE ||
    shape?.toUpperCase() == SP.SQUARE ||
    shape == 'rectangular' ||
    shape == 'Rectangular'
  );
};

/**
 * Back compatible shape checking method for `ROUNDRECT|roundrect|round-rect`
 * @param shape
 * @returns
 */
export const isRoundRectShape = (shape: string | undefined) => {
  return (
    shape == SP.RNDREC ||
    shape?.toUpperCase() == SP.RNDREC ||
    shape == 'round-rect' // back-compatible
  );
};

export const isFlexShape = (shape: string | undefined) => {
  return shape == SP.FLEX || shape?.toUpperCase() == SP.FLEX;
};

/**
 * Safe way to check shape relevant field
 *
 * @param shape could be capitalized format, but no problem
 * @param field
 * @returns if field editable
 */
export const checkFieldBelongBy = (
  shape: string | undefined,
  field: string,
) => {
  if (!shape) return true; // assume its a string field and editable
  if (field === 'system') return true;
  const notFoundInSquare = !OMIT_FROM_ROUND.includes(field);
  const notFoundInRound = !OMIT_FROM_SQUARE.includes(field);
  // shape irrelavant field assume its visible! - 2025/02/27
  if (notFoundInRound && notFoundInSquare) return true;
  if (isRoundShape(shape)) return OMIT_FROM_SQUARE.includes(field);
  if (isRectShape(shape)) return OMIT_FROM_ROUND.includes(field);
  // ! consider ROUNDRECT shape case:
  // @2025/09/08
  if (isRoundRectShape(shape)) {
    return !OMIT_FROM_ROUNDRECT.includes(field);
  }
  return true; // visible to allow edit
};

/**
 * Filter out unrelated fields with shape
 *
 * @param fields fields to filter out
 * @param shape shape in use
 * @returns new set of field after shape filter
 */
export const getReducedFieldsByShape = (
  fields: string[],
  shape: string | undefined | '',
) => {
  if (!shape) return fields;
  let endFields = fields;
  if (isRoundShape(shape)) {
    endFields = _.difference(fields, OMIT_FROM_ROUND);
  }
  if (isRectShape(shape)) {
    endFields = _.difference(fields, OMIT_FROM_SQUARE);
  }
  if (isRoundRectShape(shape)) {
    endFields = _.difference(fields, OMIT_FROM_RDRC);
  }
  // ! check FLEX shape to derive fields - 2026/03/26
  if (isFlexShape(shape)) {
    endFields = _.difference(fields, OMIT_FROM_ROUND);
  }
  // ! remove `shape` at last
  // @2025/06/11
  return endFields.filter((f) => f !== 'shape');
};

export const getOppositeFields = (field: string) => {
  if (field.startsWith('diameter')) {
    return ['width_1', 'width_2', 'height_1', 'height_2'];
  }
  if (field.startsWith('width') || field.startsWith('height')) {
    return ['diameter_1', 'diameter_2'];
  }
  return [];
};

/**
 * Get shape related dimension fields
 * @param shape fitting shape: round, or square
 * @returns
 */
export const ductFieldsOmmitedBy = (shape: string) => {
  if (isRoundShape(shape)) return OMIT_FROM_ROUND;
  if (isRectShape(shape)) return OMIT_FROM_SQUARE;
  if (isFlexShape(shape)) return OMIT_FROM_ROUND;
  return [];
};

export const isAngleField = (field: string) => field == FIELD_A0;

export const hasSymbolDiffuserCode = (detail: GO): boolean => {
  return !!detail[FIELD_DF];
};

/**
 * True if any dimension field (width / height / diameter) is non-empty.
 * Used for spec-based color rendering. @2026/04/09
 */
export const hasSymbolSize = (detail: GO): boolean =>
  !!(detail[FIELD_W1] || detail[FIELD_H1] || detail[FIELD_D1]);

/**
 * True if the `system` field is non-empty.
 * Used for spec-based color rendering. @2026/04/09
 */
export const hasSymbolSystem = (detail: GO): boolean => !!detail[FIELD_S0];

/**
 * Derive the spec-based symbol version from its detail.
 * Call this whenever a symbol's detail changes (correction, save, etc.)
 * to keep the version in sync with the color rules.
 *
 * Priority (highest first):
 *  no size + no system → REV.R  (red)
 *  no size             → REV.BL (dark blue)
 *  no system           → REV.AQ (aqua)
 *  both present        → REV.TQ (turquoise)
 *
 * @2026/04/09
 */
export const resolveSpecVersion = (detail: GO): REV => {
  const size = hasSymbolSize(detail);
  const system = hasSymbolSystem(detail);
  // ! insulation zone always use manual version
  // @2026/07/15
  if (detail['category'] == CAT.Z) return REV.M;

  if (!size && !system) return REV.R;
  if (!size) return REV.BL;
  if (!system) return REV.AQ;
  return REV.TQ;
};

/**
 * ! Restrict the input length in a hard way!
 * @date 2025/10/22
 * @param field field name
 * @param value field value
 * @returns pass or not
 */
export const inputLengthCheck = (field: string, value: string) => {
  if (field == FIELD_MP && value.length > 3) return false;
  return true;
};

/**
 * Build new pipe line info from active line
 *
 * @param details pipe line base info
 * @returns
 */
export const cloneLineBaseProperties = (details: GO): GO => {
  const shape = details['shape'] || '';
  const dimension: GO = { shape };
  //  FIXME: check `PIPE_LENGTH` to have diemeter property!
  // @date 2024/07/08
  const isPipeLine = details['entityType'] === LENGTH_TYPE.P;
  if (isRoundShape(shape) || isPipeLine) {
    dimension['diameter_1'] = details['diameter_1'] || '';
  }
  if (isRectShape(shape)) {
    dimension['width_1'] = details['width_1'] || '';
    dimension['height_1'] = details['height_1'] || '';
  }
  return {
    category: details['category'],
    entityType: details['entityType'],
    icon: details['icon'],
    system: details['system'],
    toolType: details['toolType'],
    ...dimension,
  };
};

/**
 * Organize list of symbols by entity type
 *
 * @param symbols symbols details list
 * @param includeLength if features have `???_LENGTH` item
 *
 * @returns group symbols by entity type
 */
export const findEntityTypesFromSymbols = (
  symbols: GO[],
  includeLength = false,
) => {
  const groups: EntityGroup = {};

  symbols.forEach((f) => {
    const { entityType } = f as FittingEntity;
    if (entityType === 'LABEL') return console.warn('## got LABEL!');
    if (!entityType)
      return console.warn(`## got empty entityType: ${entityType}`);

    const groupByType = groups[entityType];
    // blank list init
    if (!groupByType) groups[entityType] = [];
    groups[entityType].push(f);
  });

  if (includeLength) {
    return {
      groups,
      types: Object.keys(groups),
    };
  }

  const excludePipeLength = Object.keys(groups).filter(
    (t) => t !== LENGTH_TYPE.D && t !== LENGTH_TYPE.P,
  );

  return {
    groups,
    types: excludePipeLength,
  };
};

/**
 * Find the common value of each field among the symbols
 * @param symbols
 * @param fieldsAfterShapeChecking
 * @returns
 */
export const getCommonValueFromSymbols = (
  symbols: GO[],
  fieldsAfterShapeChecking: string[],
) => {
  // figure out the common values for each field
  const dimensionValuesByField: { [key: string]: Set<string> } = {};
  fieldsAfterShapeChecking.forEach((f) => {
    const values = dimensionValuesByField[f];
    if (!values) dimensionValuesByField[f] = new Set();
    symbols.forEach((detail) => dimensionValuesByField[f].add(detail[f]));
  });
  return fieldsAfterShapeChecking.reduce((prev: GO, curr: string) => {
    const values = dimensionValuesByField[curr];
    prev[curr] = values.size > 1 ? '' : [...values][0];
    return prev;
  }, {});
};

/**
 * Find out common fields derived from `shape`
 * @deprecated in favor of `shape` swith inside of group - 2025/06/11
 * @param symbols
 * @param commonFields
 * @returns
 */
export const getReducedFieldsFromSymbols = (
  symbols: GO[],
  commonFields: string[],
  useSystemField = true,
) => {
  // FIXME: get fields without `length_xxx` field
  // cos `length_xxx` field has no need to correct!
  // @2023/11/17
  const fieldsWithoutLengthX = commonFields.filter(
    (f) => !f.startsWith('length_'),
  );
  const correctedFields = useSystemField
    ? ['system', ...fieldsWithoutLengthX]
    : fieldsWithoutLengthX;
  const shapesFromSymbols = new Set<string>();
  symbols.forEach((smb) => smb.shape && shapesFromSymbols.add(smb.shape));
  if (shapesFromSymbols.size == 0) return correctedFields;

  const theCommonShape = shapesFromSymbols.values().next().value;
  const fieldsFromShape = getReducedFieldsByShape(
    correctedFields,
    theCommonShape,
  );
  return fieldsFromShape;
};

/**
 * filter out read-only fields and only show editable fields
 * @param fields fields including: `length_1`, `length_unit_str`
 * @date 2025/06/11
 * @returns
 */
export const stripLengthFields = (fields: string[]) => {
  // return fields.filter((f) => !f.startsWith('length_'));
  return _.difference(fields, OMIT_FROM_EDIT);
};
